import React, { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { Check, ChevronDown } from "lucide-react";

const ChoiceLogoPicker = ({
  mode = "radio",
  options = [],
  logos = [],
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [draftValues, setDraftValues] = useState([]);

  const isCheckbox = mode === "checkbox";

  useEffect(() => {
    if (!open) return;

    if (isCheckbox) {
      setDraftValues(Array.isArray(value) ? [...value] : []);
    } else {
      setDraftValues(value ? [value] : []);
    }
  }, [open, isCheckbox, value]);

  const hasSelection = isCheckbox
    ? Array.isArray(value) && value.length > 0
    : Boolean(value);

  const displayText = () => {
    if (isCheckbox) {
      const selected = Array.isArray(value) ? value : [];
      if (selected.length === 0) return placeholder;
      if (selected.length <= 2) return selected.join(", ");
      
      return `${selected.slice(0, 2).join(", ")}, ...`;
    }

    return value || placeholder;
  };

  const displayTitle = () => {
    if (isCheckbox) {
      const selected = Array.isArray(value) ? value : [];
      return selected.length > 0 ? selected.join(", ") : undefined;
    }

    return value || undefined;
  };

  const tileSelected = (option) => {
    const source = open
      ? draftValues
      : isCheckbox
        ? Array.isArray(value)
          ? value
          : []
        : value
          ? [value]
          : [];

    return source.includes(option);
  };

  const handleTileClick = (option) => {
    if (isCheckbox) {
      setDraftValues((prev) => {
        const next = [...prev];
        const idx = next.indexOf(option);
        if (idx >= 0) {
          next.splice(idx, 1);
        } else {
          next.push(option);
        }
        return next;
      });
      return;
    }

    setDraftValues([option]);
  };

  const handleConfirm = () => {
    if (isCheckbox) {
      onChange(draftValues);
    } else {
      onChange(draftValues[0] ?? null);
    }

    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="flex h-11 w-full items-center justify-between rounded-xl border border-[#d9d9d9] bg-white px-3 text-left text-base shadow-sm transition-colors hover:border-[#4096ff] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          title={displayTitle()}
          className={`min-w-0 flex-1 truncate pr-2 ${
            hasSelection ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {displayText()}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
      </button>

      <Modal
        open={open}
        title={placeholder}
        onCancel={() => setOpen(false)}
        footer={[
          <Button
            key="done"
            type="primary"
            className="rounded-lg w-full"
            onClick={handleConfirm}
          >
            Submit
          </Button>,
        ]}
        width={
          // Previous mobile width:
          // typeof window !== "undefined" && window.innerWidth < 640
          //   ? Math.max(window.innerWidth - 16, 320)
          //   : Math.min(
          //       560,
          //       typeof window !== "undefined" ? window.innerWidth - 32 : 560,
          //     )
          Math.min(
            560,
            typeof window !== "undefined" ? window.innerWidth - 32 : 560,
          )
        }
        centered
        destroyOnClose
        // Previous mobile modal body:
        // className="choice-logo-picker-modal [&_.ant-modal-body]:flex [&_.ant-modal-body]:flex-col [&_.ant-modal-body]:overflow-hidden [&_.ant-modal-body]:!max-h-[min(85vh,720px)] sm:[&_.ant-modal-body]:!max-h-none"
        className="choice-logo-picker-modal"
        styles={{
          body: {
            paddingTop: 8,
            paddingBottom: 8,
          },
        }}
      >
        {/* Previous mobile list layout:
        <div className="min-h-[min(60vh,480px)] max-h-[min(70vh,560px)] flex-1 overflow-y-auto overscroll-contain border-b border-gray-200 pb-3 pt-1 sm:min-h-0 sm:max-h-none sm:overflow-visible">
          <div className="flex flex-col gap-2 sm:grid sm:grid-cols-3 sm:gap-3">
            ... tile used flex row on mobile:
            className={`relative flex w-full items-center gap-3 rounded-xl border bg-white p-3 text-left transition-all hover:bg-gray-50 sm:flex-col sm:items-center sm:text-center`}
            check: className="absolute right-2 top-2 ... sm:right-1.5 sm:top-1.5"
            image: className="h-14 w-14 ... sm:mb-2 sm:h-16 sm:w-16"
            label: className="min-w-0 flex-1 pr-6 text-sm ... sm:line-clamp-3 sm:w-full sm:pr-0 sm:text-center sm:text-xs md:text-sm"
          </div>
        </div>
        */}
        <div className="border-b border-gray-200 pb-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            {options.map((option, idx) => {
              const selected = tileSelected(option);
              const logoUrl =
                typeof logos[idx] === "string" ? logos[idx].trim() : "";

              return (
                <button
                  key={`${option}-${idx}`}
                  type="button"
                  onClick={() => handleTileClick(option)}
                  className={`relative flex w-full flex-col items-center rounded-xl border bg-white p-3 text-center transition-all hover:bg-gray-50 ${
                    selected
                      ? "border-2 border-gray-600 shadow-sm"
                      : "border border-gray-200"
                  }`}
                >
                  {selected ? (
                    <span
                      className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-700 text-white"
                      aria-hidden
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                  ) : null}
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      className="mb-2 h-16 w-16 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="mb-2 flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs text-gray-400"
                      aria-hidden
                    >
                      No logo
                    </div>
                  )}
                  <span className="line-clamp-3 w-full text-center text-xs leading-snug text-gray-800 md:text-sm">
                    {option}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ChoiceLogoPicker;
